/**
 * DocApi — REST client for backend-driven documentation.
 *
 * Backend tables: doc_article, doc_module_registry, doc_ui_label
 * Endpoints:
 *   GET    /api/v1/documentation/articles/resolve?route=...&locale=...
 *   GET    /api/v1/documentation/articles/{moduleCode}/{routeKey}?locale=...
 *   GET    /api/v1/documentation/articles?moduleCode=...&category=...&locale=...
 *   GET    /api/v1/documentation/modules
 *   GET    /api/v1/documentation/labels?locale=...
 *   PUT    /api/v1/documentation/articles/{moduleCode}/{routeKey}
 *   POST   /api/v1/documentation/articles/{moduleCode}/{routeKey}/publish
 *   DELETE /api/v1/documentation/articles/{moduleCode}/{routeKey}
 *   POST   /api/v1/documentation/module-seed
 *   GET    /api/v1/documentation/search?q=...
 *
 * Falls back to null/empty on any error — zero-risk default.
 */

import axios from 'axios';
import { buildSaasHeaders, resolveSaasContext } from '@/uptk/core/api/saasContext';
import { toModuleInactiveError } from '@/uptk/core/api/moduleInactive';

// ─── Types ─────────────────────────────────────────────────────────

/** Content block types matching backend DocModels.DocContentBlock */
export type DocContentBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'callout'; variant: 'info' | 'warning' | 'tip' | 'danger'; text: string }
  | { type: 'code'; language: string; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'steps'; items: { id?: string; text: string; result?: string }[] }
  | { type: 'image'; url: string; alt: string }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'video'; url: string; title: string };

/** A section within an article (matches backend DocContent.sections) */
export interface DocSection {
  id: string;
  heading: string;
  blocks: DocContentBlock[];
}

/** Full article content (matches backend DocContent) */
export interface DocContent {
  title: string;
  subtitle: string;
  icon?: string;
  sections: DocSection[];
  relatedArticles?: string[];
  tags?: string[];
}

/** Audience gate (matches backend DocAudienceResolver / doc_article.audience) — who can see the article. */
export type DocAudience = 'public' | 'partner' | 'maker' | 'distributor' | 'platform';

/** Full article response (matches backend DocArticleResponse) */
export interface DocArticleResponse {
  routeKey: string;
  moduleCode: string;
  locale: string;
  title: string;
  subtitle: string;
  icon?: string;
  sections: DocSection[];
  relatedArticles?: string[];
  tags?: string[];
  category: string;
  sortOrder: number;
  version: number;
  source: 'seed' | 'ai' | 'manual';
  published: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
  audience: DocAudience;
}

/** F1 resolve response (matches backend DocResolveResponse) */
export interface DocResolveResponse {
  requestedRoute: string;
  matchedRouteKey: string;
  matchLevel: 'entity_action' | 'entity' | 'domain';
  article: DocArticleResponse;
}

/** Article summary for list views (matches backend DocArticleSummary) */
export interface DocArticleSummary {
  routeKey: string;
  moduleCode: string;
  locale: string;
  title: string;
  subtitle: string;
  icon?: string;
  category: string;
  sortOrder: number;
  tags?: string[];
  audience: DocAudience;
}

/** Module info (matches backend DocModuleInfo) */
export interface DocModuleInfo {
  moduleCode: string;
  displayName: string;
  icon?: string;
  description?: string;
  docCount: number;
}

/** Upsert request (matches backend DocArticleUpsertRequest) */
export interface DocArticleUpsertRequest {
  locale: string;
  category: 'guide' | 'reference' | 'tutorial' | 'changelog' | 'api';
  sortOrder?: number;
  content: DocContent;
  source?: 'ai' | 'manual';
  /** Defaults to 'public' on the backend when omitted. */
  audience?: DocAudience;
}

/** Module seed request (matches backend DocModuleSeedRequest) */
export interface DocModuleSeedRequest {
  module: {
    moduleCode: string;
    displayName: string;
    icon?: string;
    description?: string;
  };
  articles: Array<{
    routeKey: string;
    locale: string;
    category: string;
    sortOrder?: number;
    content: DocContent;
    published?: boolean;
    audience?: DocAudience;
  }>;
}

/** Module seed response (matches backend DocModuleSeedResponse) */
export interface DocModuleSeedResponse {
  moduleCode: string;
  results: {
    articlesCreated: number;
    articlesUpdated: number;
    articlesUnchanged: number;
    moduleRegistered: boolean;
  };
}

/** Search result (matches backend DocSearchResult) */
export interface DocSearchResult {
  routeKey: string;
  moduleCode: string;
  locale: string;
  title: string;
  subtitle: string;
  icon?: string;
  category: string;
  relevance: number;
}

// ─── Internal Helpers ──────────────────────────────────────────────

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const normalizeNullableString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const normalizeStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }
  return [];
};

const DOC_AUDIENCES: DocAudience[] = ['public', 'partner', 'maker', 'distributor', 'platform'];

const normalizeAudience = (value: unknown): DocAudience =>
  DOC_AUDIENCES.includes(value as DocAudience) ? (value as DocAudience) : 'public';

const normalizeDocContentBlock = (raw: unknown): DocContentBlock => {
  if (!isRecord(raw)) {
    return { type: 'paragraph', text: '' };
  }
  const type = normalizeString(raw.type, 'paragraph');
  switch (type) {
    case 'paragraph':
      return { type: 'paragraph', text: normalizeString(raw.text) };
    case 'callout': {
      const v = normalizeString(raw.variant);
      return {
        type: 'callout' as const,
        variant: (['info', 'warning', 'tip', 'danger'].includes(v) ? v : 'info') as (DocContentBlock & { type: 'callout' })['variant'],
        text: normalizeString(raw.text),
      };
    }
    case 'code':
      return { type: 'code', language: normalizeString(raw.language, 'text'), text: normalizeString(raw.text) };
    case 'list':
      return { type: 'list', items: normalizeStringArray(raw.items) };
    case 'steps': {
      const items = Array.isArray(raw.items)
        ? raw.items.map((item: unknown) => {
            if (isRecord(item)) {
              return {
                id: normalizeNullableString(item.id) ?? undefined,
                text: normalizeString(item.text),
                result: normalizeNullableString(item.result),
              };
            }
            return { text: '' };
          })
        : [];
      return { type: 'steps', items };
    }
    case 'image':
      return { type: 'image', url: normalizeString(raw.url), alt: normalizeString(raw.alt) };
    case 'table':
      return {
        type: 'table',
        headers: normalizeStringArray(raw.headers),
        rows: Array.isArray(raw.rows)
          ? raw.rows.map((row: unknown) => normalizeStringArray(row))
          : [],
      };
    case 'video':
      return { type: 'video', url: normalizeString(raw.url), title: normalizeString(raw.title) };
    default:
      return { type: 'paragraph', text: normalizeString(raw.text) };
  }
};

const normalizeDocSection = (raw: unknown): DocSection => {
  if (!isRecord(raw)) {
    return { id: '', heading: '', blocks: [] };
  }
  return {
    id: normalizeString(raw.id),
    heading: normalizeString(raw.heading),
    blocks: Array.isArray(raw.blocks)
      ? raw.blocks.map(normalizeDocContentBlock)
      : [],
  };
};

export const normalizeArticleResponse = (raw: unknown): DocArticleResponse => {
  if (!isRecord(raw)) {
    return {
      routeKey: '', moduleCode: '', locale: 'en',
      title: '', subtitle: '', sections: [],
      category: 'guide', sortOrder: 100,
      version: 0, source: 'seed', published: false,
      updatedAt: null, updatedBy: null, audience: 'public',
    };
  }
  return {
    routeKey: normalizeString(raw.routeKey),
    moduleCode: normalizeString(raw.moduleCode),
    locale: normalizeString(raw.locale, 'en'),
    title: normalizeString(raw.title),
    subtitle: normalizeString(raw.subtitle),
    icon: normalizeNullableString(raw.icon),
    sections: Array.isArray(raw.sections)
      ? raw.sections.map(normalizeDocSection)
      : [],
    relatedArticles: normalizeStringArray(raw.relatedArticles),
    tags: normalizeStringArray(raw.tags),
    category: normalizeString(raw.category, 'guide'),
    sortOrder: typeof raw.sortOrder === 'number' ? raw.sortOrder : 100,
    version: typeof raw.version === 'number' ? raw.version : 0,
    source: ['seed', 'ai', 'manual'].includes(normalizeString(raw.source))
      ? raw.source as DocArticleResponse['source']
      : 'seed',
    published: typeof raw.published === 'boolean' ? raw.published : false,
    updatedAt: normalizeNullableString(raw.updatedAt),
    updatedBy: normalizeNullableString(raw.updatedBy),
    audience: normalizeAudience(raw.audience),
  };
};

const normalizeArticleSummary = (raw: unknown): DocArticleSummary => {
  if (!isRecord(raw)) {
    return { routeKey: '', moduleCode: '', locale: 'en', title: '', subtitle: '', category: 'guide', sortOrder: 100, audience: 'public' };
  }
  return {
    routeKey: normalizeString(raw.routeKey),
    moduleCode: normalizeString(raw.moduleCode),
    locale: normalizeString(raw.locale, 'en'),
    title: normalizeString(raw.title),
    subtitle: normalizeString(raw.subtitle),
    icon: normalizeNullableString(raw.icon),
    category: normalizeString(raw.category, 'guide'),
    sortOrder: typeof raw.sortOrder === 'number' ? raw.sortOrder : 100,
    tags: normalizeStringArray(raw.tags),
    audience: normalizeAudience(raw.audience),
  };
};

const normalizeModuleInfo = (raw: unknown): DocModuleInfo => {
  if (!isRecord(raw)) {
    return { moduleCode: '', displayName: '', docCount: 0 };
  }
  return {
    moduleCode: normalizeString(raw.moduleCode),
    displayName: normalizeString(raw.displayName),
    icon: normalizeNullableString(raw.icon),
    description: normalizeNullableString(raw.description),
    docCount: typeof raw.docCount === 'number' ? raw.docCount : 0,
  };
};

const normalizeResolveResponse = (raw: unknown): DocResolveResponse => {
  if (!isRecord(raw)) {
    return {
      requestedRoute: '',
      matchedRouteKey: '',
      matchLevel: 'domain',
      article: normalizeArticleResponse(null),
    };
  }
  return {
    requestedRoute: normalizeString(raw.requestedRoute),
    matchedRouteKey: normalizeString(raw.matchedRouteKey),
    matchLevel: ['entity_action', 'entity', 'domain'].includes(normalizeString(raw.matchLevel))
      ? raw.matchLevel as DocResolveResponse['matchLevel']
      : 'domain',
    article: normalizeArticleResponse(raw.article),
  };
};

const normalizeSearchResult = (raw: unknown): DocSearchResult => {
  if (!isRecord(raw)) {
    return { routeKey: '', moduleCode: '', locale: 'en', title: '', subtitle: '', category: 'guide', relevance: 0 };
  }
  return {
    routeKey: normalizeString(raw.routeKey),
    moduleCode: normalizeString(raw.moduleCode),
    locale: normalizeString(raw.locale, 'en'),
    title: normalizeString(raw.title),
    subtitle: normalizeString(raw.subtitle),
    icon: normalizeNullableString(raw.icon),
    category: normalizeString(raw.category, 'guide'),
    relevance: typeof raw.relevance === 'number' ? raw.relevance : 0,
  };
};

const buildHeaders = () => {
  const context = resolveSaasContext({});
  return buildSaasHeaders(context);
};

// ─── Negative-Result Cache ──────────────────────────────────────────

let docCacheEpoch = 0;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const resolveCache = new Map<string, { data: DocResolveResponse; timestamp: number }>();
const articleCache = new Map<string, { data: DocArticleResponse; timestamp: number }>();
const notFoundCache = new Map<string, number>();

const isCacheExpired = (timestamp: number): boolean =>
  Date.now() - timestamp > CACHE_TTL_MS;

/**
 * Clear all documentation caches. Call after upsert/publish/delete.
 */
export function clearDocCache(): void {
  docCacheEpoch += 1;
  resolveCache.clear();
  articleCache.clear();
  notFoundCache.clear();
}

// ─── API Functions ─────────────────────────────────────────────────

/**
 * Resolve documentation for a frontend route (F1 help).
 * Returns null if no article matches.
 *
 * GET /api/v1/documentation/articles/resolve?route=...&locale=...
 */
export async function resolveHelp(
  route: string,
  locale: string = 'en',
): Promise<DocResolveResponse | null> {
  if (!route.trim()) return null;

  const epoch = docCacheEpoch;
  const cacheKey = `resolve.${route}.${locale}`;
  const cached = resolveCache.get(cacheKey);
  if (cached && !isCacheExpired(cached.timestamp)) {
    return cached.data;
  }

  if (notFoundCache.has(cacheKey) && !isCacheExpired(notFoundCache.get(cacheKey)!)) {
    return null;
  }

  try {
    const response = await axios.get<unknown>(
      '/api/v1/documentation/articles/resolve',
      { params: { route, locale }, headers: buildHeaders() },
    );
    const result = normalizeResolveResponse(response.data);
    if (epoch === docCacheEpoch) resolveCache.set(cacheKey, { data: result, timestamp: Date.now() });
    notFoundCache.delete(cacheKey);
    return result;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      if (epoch === docCacheEpoch) notFoundCache.set(cacheKey, Date.now());
      return null;
    }
    console.warn('[DocApi] resolveHelp failed:', error instanceof Error ? error.message : error);
    return null;
  }
}

/**
 * Result of a direct article fetch: the article, or — UPT-2069 (L7) — the module that is not activated for the tenant
 * when the server refuses the section with 403 `module.inactive` (the page then shows « module non activé »).
 */
export interface DocArticleFetch {
  article: DocArticleResponse | null;
  /** Canonical code of the closed module, or null. */
  inactiveModule: string | null;
}

/**
 * Get a published documentation article, telling apart « not found » and « module not activated » (UPT-2069 L7:
 * the server answers 403 `module.inactive` for an article of a section whose module is closed for the tenant).
 *
 * GET /api/v1/documentation/articles/{moduleCode}/{routeKey}?locale=...
 */
export async function fetchDocArticleState(
  moduleCode: string,
  routeKey: string,
  locale: string = 'en',
): Promise<DocArticleFetch> {
  if (!moduleCode.trim() || !routeKey.trim()) return { article: null, inactiveModule: null };

  const epoch = docCacheEpoch;
  const cacheKey = `article.${moduleCode}.${routeKey}.${locale}`;
  const cached = articleCache.get(cacheKey);
  if (cached && !isCacheExpired(cached.timestamp)) {
    return { article: cached.data, inactiveModule: null };
  }

  try {
    const response = await axios.get<unknown>(
      `/api/v1/documentation/articles/${encodeURIComponent(moduleCode)}/${encodeURIComponent(routeKey)}`,
      { params: { locale }, headers: buildHeaders() },
    );
    const result = normalizeArticleResponse(response.data);
    if (epoch === docCacheEpoch) articleCache.set(cacheKey, { data: result, timestamp: Date.now() });
    return { article: result, inactiveModule: null };
  } catch (error) {
    const inactive = toModuleInactiveError(error, moduleCode);
    if (inactive !== null) {
      return { article: null, inactiveModule: inactive.module };
    }
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return { article: null, inactiveModule: null };
    }
    console.warn('[DocApi] fetchDocArticle failed:', error instanceof Error ? error.message : error);
    return { article: null, inactiveModule: null };
  }
}

/**
 * Get a published documentation article (null when absent, refused or failed).
 *
 * GET /api/v1/documentation/articles/{moduleCode}/{routeKey}?locale=...
 */
export async function fetchDocArticle(
  moduleCode: string,
  routeKey: string,
  locale: string = 'en',
): Promise<DocArticleResponse | null> {
  return (await fetchDocArticleState(moduleCode, routeKey, locale)).article;
}

/**
 * List published documentation articles.
 *
 * GET /api/v1/documentation/articles?moduleCode=...&locale=...&category=...
 */
export async function listDocArticles(
  moduleCode?: string,
  locale?: string,
  category?: string,
): Promise<DocArticleSummary[]> {
  try {
    const params: Record<string, string> = {};
    if (moduleCode) params.moduleCode = moduleCode;
    if (locale) params.locale = locale;
    if (category) params.category = category;

    const response = await axios.get<unknown[]>(
      '/api/v1/documentation/articles',
      { params, headers: buildHeaders() },
    );
    return Array.isArray(response.data)
      ? response.data.map(normalizeArticleSummary)
      : [];
  } catch (error) {
    console.warn('[DocApi] listDocArticles failed:', error instanceof Error ? error.message : error);
    return [];
  }
}

/**
 * List all registered documentation modules.
 *
 * GET /api/v1/documentation/modules
 */
export async function listDocModules(): Promise<DocModuleInfo[]> {
  try {
    const response = await axios.get<unknown[]>(
      '/api/v1/documentation/modules',
      { headers: buildHeaders() },
    );
    return Array.isArray(response.data)
      ? response.data.map(normalizeModuleInfo)
      : [];
  } catch (error) {
    console.warn('[DocApi] listDocModules failed:', error instanceof Error ? error.message : error);
    return [];
  }
}

/**
 * Result for upsertDocArticle. ok=true means success; ok=false carries the HTTP
 * errorStatus so callers can show accurate copy (network vs. authz vs. server).
 */
export type UpsertDocResult = {
  ok: boolean;
  article: DocArticleResponse | null;
  errorStatus: number | null;
};

/**
 * Create or update a documentation article (AI agent or manual).
 *
 * PUT /api/v1/documentation/articles/{moduleCode}/{routeKey}
 */
export async function upsertDocArticle(
  moduleCode: string,
  routeKey: string,
  request: DocArticleUpsertRequest,
): Promise<UpsertDocResult> {
  try {
    const response = await axios.put<unknown>(
      `/api/v1/documentation/articles/${encodeURIComponent(moduleCode)}/${encodeURIComponent(routeKey)}`,
      request,
      { headers: buildHeaders() },
    );
    clearDocCache();
    return { ok: true, article: normalizeArticleResponse(response.data), errorStatus: null };
  } catch (error) {
    const errorStatus = axios.isAxiosError(error) ? (error.response?.status ?? null) : null;
    console.warn('[DocApi] upsertDocArticle failed:', error instanceof Error ? error.message : error);
    return { ok: false, article: null, errorStatus };
  }
}

/**
 * Publish a documentation article.
 *
 * POST /api/v1/documentation/articles/{moduleCode}/{routeKey}/publish?locale=...
 */
export async function publishDocArticle(
  moduleCode: string,
  routeKey: string,
  locale: string = 'en',
): Promise<DocArticleResponse | null> {
  try {
    const response = await axios.post<unknown>(
      `/api/v1/documentation/articles/${encodeURIComponent(moduleCode)}/${encodeURIComponent(routeKey)}/publish`,
      null,
      { params: { locale }, headers: buildHeaders() },
    );
    clearDocCache();
    return normalizeArticleResponse(response.data);
  } catch (error) {
    console.warn('[DocApi] publishDocArticle failed:', error instanceof Error ? error.message : error);
    return null;
  }
}

/**
 * Delete a documentation article.
 *
 * DELETE /api/v1/documentation/articles/{moduleCode}/{routeKey}?locale=...
 */
export async function deleteDocArticle(
  moduleCode: string,
  routeKey: string,
  locale: string = 'en',
): Promise<boolean> {
  try {
    await axios.delete(
      `/api/v1/documentation/articles/${encodeURIComponent(moduleCode)}/${encodeURIComponent(routeKey)}`,
      { params: { locale }, headers: buildHeaders() },
    );
    clearDocCache();
    return true;
  } catch (error) {
    console.warn('[DocApi] deleteDocArticle failed:', error instanceof Error ? error.message : error);
    return false;
  }
}

/**
 * Bulk-seed module documentation.
 *
 * POST /api/v1/documentation/module-seed
 */
export async function seedModuleDocs(
  request: DocModuleSeedRequest,
): Promise<DocModuleSeedResponse | null> {
  try {
    const response = await axios.post<unknown>(
      '/api/v1/documentation/module-seed',
      request,
      { headers: buildHeaders() },
    );
    clearDocCache();
    if (isRecord(response.data)) {
      return {
        moduleCode: normalizeString(response.data.moduleCode),
        results: {
          articlesCreated: typeof response.data.results === 'object' && response.data.results !== null
            ? (response.data.results as Record<string, unknown>).articlesCreated as number ?? 0
            : 0,
          articlesUpdated: typeof response.data.results === 'object' && response.data.results !== null
            ? (response.data.results as Record<string, unknown>).articlesUpdated as number ?? 0
            : 0,
          articlesUnchanged: typeof response.data.results === 'object' && response.data.results !== null
            ? (response.data.results as Record<string, unknown>).articlesUnchanged as number ?? 0
            : 0,
          moduleRegistered: typeof response.data.results === 'object' && response.data.results !== null
            ? (response.data.results as Record<string, unknown>).moduleRegistered as boolean ?? false
            : false,
        },
      };
    }
    return null;
  } catch (error) {
    console.warn('[DocApi] seedModuleDocs failed:', error instanceof Error ? error.message : error);
    return null;
  }
}

/**
 * Generate a single documentation block via AI given a prompt.
 *
 * POST /api/v1/documentation/articles/{moduleCode}/{routeKey}/draft-block
 */
export async function generateDocBlock(
  moduleCode: string,
  routeKey: string,
  prompt: string,
  locale: string = 'en',
): Promise<DocContentBlock | null> {
  try {
    const response = await axios.post<unknown>(
      `/api/v1/documentation/articles/${encodeURIComponent(moduleCode)}/${encodeURIComponent(routeKey)}/draft-block`,
      { prompt, locale },
      { headers: buildHeaders() },
    );
    return normalizeDocContentBlock(response.data);
  } catch (error) {
    console.warn('[DocApi] generateDocBlock failed:', error instanceof Error ? error.message : error);
    return null;
  }
}

/**
 * Search documentation articles.
 *
 * GET /api/v1/documentation/search?q=...&locale=...
 */
export async function searchDocArticles(
  query: string,
  locale?: string,
): Promise<DocSearchResult[]> {
  if (!query.trim()) return [];

  try {
    const params: Record<string, string> = { q: query };
    if (locale) params.locale = locale;

    const response = await axios.get<unknown[]>(
      '/api/v1/documentation/search',
      { params, headers: buildHeaders() },
    );
    return Array.isArray(response.data)
      ? response.data.map(normalizeSearchResult)
      : [];
  } catch (error) {
    console.warn('[DocApi] searchDocArticles failed:', error instanceof Error ? error.message : error);
    return [];
  }
}

// ─── UI Labels ───────────────────────────────────────────────────

/**
 * Fetch localized documentation UI labels.
 *
 * GET /api/v1/documentation/labels?locale=...
 *
 * Returns a map of label_key → localized value.
 * Example: { "doc.portal.hero.title": "Geode Platform Docs", ... }
 */
export async function fetchDocLabels(
  locale: string = 'en',
): Promise<Record<string, string>> {
  try {
    const response = await axios.get<Record<string, string>>(
      '/api/v1/documentation/labels',
      { params: { locale }, headers: buildHeaders() },
    );
    return isRecord(response.data) ? response.data : {};
  } catch (error) {
    console.warn('[DocApi] fetchDocLabels failed:', error instanceof Error ? error.message : error);
    return {};
  }
}
